import React, { useEffect, useState, useMemo } from 'react';
import { reportsApi, matchesApi, sessionsApi, clientsApi } from '../../lib/api';
import { GroupBox } from '../shared/GroupBox';
import { DenseTable } from '../shared/DenseTable';
import { TrialBalanceDetailDialog } from '../shared/TrialBalanceDetailDialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { RefreshCw, Download, FileSpreadsheet, FileText, Printer, Users, FolderTree, X, Filter, Search, Trash2, Info } from 'lucide-react';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';

// Commission Breakdown Tooltip Component
const CommissionBreakdownTooltip = ({ item, type }) => {
  const hasPatti = item.is_patti || item.patti_commission_received !== 0 || item.patti_ref_commission_received !== 0;
  const hasRefCommission = item.ref_commission_received !== 0 || item.is_reference_entry;
  const hasUserCommission = item.user_commission !== 0;
  
  // If no breakdown info, don't show tooltip
  if (!hasPatti && !hasRefCommission && !hasUserCommission) {
    return (
      <span className={`font-mono ${type === 'receive' ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
        {type === 'pay' ? '-' : ''}{item.amount.toFixed(2)}
      </span>
    );
  }
  
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={`font-mono cursor-help border-b border-dashed ${type === 'receive' ? 'text-emerald-600 dark:text-emerald-400 border-emerald-400' : 'text-red-600 dark:text-red-400 border-red-400'}`}>
            {type === 'pay' ? '-' : ''}{item.amount.toFixed(2)}
            <Info className="inline-block h-3 w-3 ml-1 opacity-50" />
          </span>
        </TooltipTrigger>
        <TooltipContent 
          side="left" 
          className="bg-slate-900 text-white p-3 rounded-lg shadow-xl max-w-xs"
        >
          <div className="space-y-2">
            <div className="font-semibold text-amber-400 border-b border-slate-700 pb-1 mb-2">
              Commission Breakdown
            </div>
            
            {/* Gross Position */}
            <div className="flex justify-between text-xs">
              <span className="text-slate-300">Gross Position:</span>
              <span className={`font-mono ${item.balance >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {item.balance >= 0 ? '+' : ''}{item.balance.toFixed(2)}
              </span>
            </div>
            
            {/* User Commission (for regular clients) */}
            {hasUserCommission && !hasPatti && (
              <div className="flex justify-between text-xs">
                <span className="text-slate-300">User Commission:</span>
                <span className={`font-mono ${item.user_commission >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {item.user_commission >= 0 ? '+' : ''}{item.user_commission.toFixed(2)}
                </span>
              </div>
            )}
            
            {/* Reference Commission Received (for reference clients) */}
            {hasRefCommission && (
              <div className="flex justify-between text-xs">
                <span className="text-slate-300">Ref Comm Received:</span>
                <span className={`font-mono ${item.ref_commission_received >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {item.ref_commission_received >= 0 ? '+' : ''}{item.ref_commission_received.toFixed(2)}
                </span>
              </div>
            )}
            
            {/* Patti Commissions */}
            {hasPatti && (
              <>
                {item.patti_commission_received !== 0 && (
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-300">Patti User Comm:</span>
                    <span className="font-mono text-emerald-400">
                      +{item.patti_commission_received.toFixed(2)}
                    </span>
                  </div>
                )}
                {item.patti_ref_commission_received !== 0 && (
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-300">Patti Ref Comm:</span>
                    <span className="font-mono text-emerald-400">
                      +{item.patti_ref_commission_received.toFixed(2)}
                    </span>
                  </div>
                )}
              </>
            )}
            
            {/* Divider and Net */}
            <div className="border-t border-slate-700 pt-2 mt-2">
              <div className="flex justify-between text-xs font-semibold">
                <span className="text-white">Net Balance:</span>
                <span className={`font-mono ${item.net_balance >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {item.net_balance >= 0 ? '+' : ''}{item.net_balance.toFixed(2)}
                </span>
              </div>
            </div>
            
            {/* Formula hint */}
            {hasPatti && hasRefCommission && (
              <div className="text-[10px] text-slate-500 mt-1 pt-1 border-t border-slate-700">
                Formula: Gross + RefComm + PattiComm
              </div>
            )}
            
            {/* Badges */}
            <div className="flex gap-1 mt-2">
              {item.is_patti && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-400">
                  Patti
                </span>
              )}
              {item.is_reference_entry && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-400">
                  Reference
                </span>
              )}
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export function TrialBalancePage() {
  // Trial Balance State
  const [matches, setMatches] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [viewMode, setViewMode] = useState('client'); // 'client' or 'group'
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [trialBalanceData, setTrialBalanceData] = useState({ receive: [], pay: [] });
  const [clientTxDialog, setClientTxDialog] = useState({ 
    open: false, 
    clientId: null, 
    clientName: null,
    matchIds: [],
    sessionIds: [],
    trialBalanceItem: null
  });

  // Handle client name click - opens detailed transaction dialog
  const handleClientClick = (clientId, clientName) => {
    // Get selected match and session IDs - keep the full IDs as the API expects them
    const selectedMatchIds = selectedItems
      .filter(id => id.startsWith('match_'));
    const selectedSessionIds = selectedItems
      .filter(id => id.startsWith('session_'));
    
    // Find the trial balance entry for this client
    const allTrialBalanceItems = [...trialBalanceData.receive, ...trialBalanceData.pay];
    const clientEntry = allTrialBalanceItems.find(item => item.client_id === clientId);
    
    setClientTxDialog({
      open: true,
      clientId,
      clientName,
      matchIds: selectedMatchIds,
      sessionIds: selectedSessionIds,
      trialBalanceItem: clientEntry
    });
  };

  // Fetch declared matches and sessions
  const fetchDeclaredItems = async () => {
    try {
      setLoading(true);
      const [matchesData, sessionsData, clientsData] = await Promise.all([
        matchesApi.list('DECLARED'),
        sessionsApi.list(null, 'DECLARED'),
        clientsApi.list()
      ]);
      
      setMatches(matchesData || []);
      setSessions(sessionsData || []);
      setClients(clientsData?.filter(c => !c.is_system_account) || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load declared matches/sessions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeclaredItems();
  }, []);

  // Combined list of declared matches and sessions for grid 1
  const declaredItems = useMemo(() => {
    const items = [];
    
    // Add matches
    matches.forEach(m => {
      const matchDate = m.declared_at || m.match_time || m.created_at;
      items.push({
        id: m.match_id,
        type: 'MATCH',
        name: m.name,
        date: matchDate,
        displayDate: matchDate ? new Date(matchDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : '-',
        displayTime: matchDate ? new Date(matchDate).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '',
        result: m.winning_team || '-'
      });
    });
    
    // Add sessions
    sessions.forEach(s => {
      const sessionDate = s.declared_at || s.created_at;
      items.push({
        id: s.session_id,
        type: 'SESSION',
        name: s.name,
        date: sessionDate,
        displayDate: sessionDate ? new Date(sessionDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : '-',
        displayTime: sessionDate ? new Date(sessionDate).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '',
        result: s.result !== null ? s.result : '-'
      });
    });
    
    // Sort by date descending
    items.sort((a, b) => new Date(b.date) - new Date(a.date));
    
    // Apply date filter
    return items.filter(item => {
      if (!dateFrom && !dateTo) return true;
      const itemDate = new Date(item.date);
      if (dateFrom && itemDate < new Date(dateFrom)) return false;
      if (dateTo && itemDate > new Date(dateTo + 'T23:59:59')) return false;
      return true;
    });
  }, [matches, sessions, dateFrom, dateTo]);

  // Filter items based on search
  const filteredDeclaredItems = useMemo(() => {
    if (!searchTerm) return declaredItems;
    return declaredItems.filter(item => 
      item.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [declaredItems, searchTerm]);

  // Toggle item selection
  const toggleItemSelection = (itemId) => {
    setSelectedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  // Select all items
  const selectAllItems = () => {
    if (selectedItems.length === filteredDeclaredItems.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(filteredDeclaredItems.map(item => item.id));
    }
  };

  // Calculate trial balance based on selected items
  const calculateTrialBalance = async () => {
    if (selectedItems.length === 0) {
      toast.error('Please select at least one match/session');
      return;
    }

    try {
      setLoading(true);
      
      // Get selected match IDs and session IDs
      const selectedMatchIds = selectedItems.filter(id => id.startsWith('match_'));
      const selectedSessionIds = selectedItems.filter(id => id.startsWith('session_'));
      
      // Fetch trial balance data from API
      const filterData = {
        match_ids: selectedMatchIds,
        session_ids: selectedSessionIds
      };
      
      const data = await reportsApi.trialBalance(filterData);
      
      if (data && data.trial_balance) {
        // Split into receive and pay, keeping full data including commission_entries
        const receive = [];
        const pay = [];
        
        data.trial_balance.forEach(item => {
          const entry = {
            client_id: item.client_id,
            name: item.client_name,
            nick_name: item.nick_name,
            amount: Math.abs(item.net_balance || item.balance || 0),
            balance: item.balance || 0,
            net_balance: item.net_balance || 0,
            user_commission: item.user_commission || 0,
            ref_commission: item.ref_commission || 0,
            commission: item.commission || 0,
            calculated_amount: item.calculated_amount || 0,
            commission_entries: item.commission_entries || [],
            entry_details: item.entry_details || [],
            is_reference_entry: item.is_reference_entry || false,
            is_patti: item.is_patti || false,
            source_transactions: item.source_transactions || [],
            // Patti-specific commission breakdown
            patti_commission_received: item.patti_commission_received || 0,
            patti_ref_commission_received: item.patti_ref_commission_received || 0,
            ref_commission_received: item.ref_commission_received || 0
          };
          
          if ((item.net_balance || item.balance || 0) > 0) {
            receive.push(entry);
          } else if ((item.net_balance || item.balance || 0) < 0) {
            pay.push(entry);
          }
        });
        
        // Sort by amount descending
        receive.sort((a, b) => b.amount - a.amount);
        pay.sort((a, b) => b.amount - a.amount);
        
        setTrialBalanceData({ receive, pay });
      }
    } catch (error) {
      console.error('Error calculating trial balance:', error);
      toast.error('Failed to calculate trial balance');
    } finally {
      setLoading(false);
    }
  };

  // Clear filters
  const clearFilters = () => {
    setDateFrom('');
    setDateTo('');
    setSearchTerm('');
    setSelectedItems([]);
    setTrialBalanceData({ receive: [], pay: [] });
  };

  // Calculate totals
  const totalReceive = useMemo(() => 
    trialBalanceData.receive.reduce((sum, item) => sum + item.amount, 0), 
    [trialBalanceData.receive]
  );
  
  const totalPay = useMemo(() => 
    trialBalanceData.pay.reduce((sum, item) => sum + item.amount, 0), 
    [trialBalanceData.pay]
  );
  
  const finalBalance = totalReceive - totalPay;

  // Export functions
  const handlePrint = () => {
    window.print();
  };

  const handleExportExcel = () => {
    if (trialBalanceData.receive.length === 0 && trialBalanceData.pay.length === 0) {
      toast.error('No data to export');
      return;
    }

    const wb = XLSX.utils.book_new();
    
    // Receive sheet
    const receiveData = [
      ['To Receive'],
      ['Name', 'Amount'],
      ...trialBalanceData.receive.map(r => [r.name, r.amount]),
      ['Total', totalReceive]
    ];
    const wsReceive = XLSX.utils.aoa_to_sheet(receiveData);
    XLSX.utils.book_append_sheet(wb, wsReceive, 'To Receive');
    
    // Pay sheet
    const payData = [
      ['To Pay'],
      ['Name', 'Amount'],
      ...trialBalanceData.pay.map(p => [p.name, p.amount]),
      ['Total', totalPay]
    ];
    const wsPay = XLSX.utils.aoa_to_sheet(payData);
    XLSX.utils.book_append_sheet(wb, wsPay, 'To Pay');
    
    // Summary sheet
    const summaryData = [
      ['Trial Balance Summary'],
      [''],
      ['Total to Receive', totalReceive],
      ['Total to Pay', totalPay],
      ['Final Balance', finalBalance],
      [''],
      ['Date Range', `${dateFrom || 'Start'} to ${dateTo || 'End'}`],
      ['Generated', new Date().toLocaleString()]
    ];
    const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');
    
    XLSX.writeFile(wb, `trial_balance_${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success('Excel exported successfully');
  };

  const handleExportPDF = () => {
    if (trialBalanceData.receive.length === 0 && trialBalanceData.pay.length === 0) {
      toast.error('No data to export');
      return;
    }

    const doc = new jsPDF();
    
    // Title
    doc.setFontSize(18);
    doc.setTextColor(212, 175, 55);
    doc.text('YellowGold Pro - Trial Balance', 14, 20);
    
    // Date range
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Period: ${dateFrom || 'Start'} to ${dateTo || 'End'}`, 14, 28);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 34);
    
    let yPos = 44;
    
    // To Receive section
    doc.setFontSize(12);
    doc.setTextColor(0, 128, 0);
    doc.text('To Receive', 14, yPos);
    yPos += 4;
    
    if (trialBalanceData.receive.length > 0) {
      doc.autoTable({
        head: [['Name', 'Amount']],
        body: trialBalanceData.receive.map(r => [r.name, r.amount.toFixed(2)]),
        startY: yPos,
        styles: { fontSize: 9, cellPadding: 2 },
        headStyles: { fillColor: [76, 175, 80], textColor: [255, 255, 255] },
        columnStyles: { 1: { halign: 'right' } },
        margin: { left: 14, right: 105 }
      });
      yPos = doc.lastAutoTable.finalY + 4;
      doc.setFontSize(10);
      doc.text(`Total: ${totalReceive.toFixed(2)}`, 14, yPos);
    }
    
    // To Pay section (on right side)
    doc.setFontSize(12);
    doc.setTextColor(220, 53, 69);
    doc.text('To Pay', 110, 44);
    
    if (trialBalanceData.pay.length > 0) {
      doc.autoTable({
        head: [['Name', 'Amount']],
        body: trialBalanceData.pay.map(p => [p.name, p.amount.toFixed(2)]),
        startY: 48,
        styles: { fontSize: 9, cellPadding: 2 },
        headStyles: { fillColor: [220, 53, 69], textColor: [255, 255, 255] },
        columnStyles: { 1: { halign: 'right' } },
        margin: { left: 110, right: 14 }
      });
    }
    
    // Final Balance
    const finalY = Math.max(doc.lastAutoTable?.finalY || 80, yPos) + 20;
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text('Final Balance:', 14, finalY);
    doc.setFontSize(16);
    doc.setTextColor(finalBalance >= 0 ? [0, 128, 0] : [220, 53, 69]);
    doc.text(finalBalance.toFixed(2), 60, finalY);
    
    doc.save(`trial_balance_${new Date().toISOString().slice(0, 10)}.pdf`);
    toast.success('PDF exported successfully');
  };

  const handleExit = () => {
    // Navigate back or close
    window.history.back();
  };

  return (
    <div className="space-y-4" data-testid="trial-balance-page">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold">
          Trial Balance <span className="kbd ml-2">F8</span>
        </h1>
      </div>

      {/* Trial Balance Content - No Tabs */}
      <div className="space-y-4">
          {/* Action Buttons Bar */}
          <div className="flex items-center gap-2 p-2 bg-card border rounded-lg flex-wrap">
            <Button 
              variant={viewMode === 'client' ? 'default' : 'outline'} 
              size="sm"
              onClick={() => setViewMode('client')}
              data-testid="btn-client-wise"
            >
              <Users className="h-4 w-4 mr-2" />
              Client wise
            </Button>
            <Button 
              variant={viewMode === 'group' ? 'default' : 'outline'} 
              size="sm"
              onClick={() => setViewMode('group')}
              data-testid="btn-group-wise"
            >
              <FolderTree className="h-4 w-4 mr-2" />
              Group wise
            </Button>
            
            <div className="h-6 w-px bg-border mx-2" />
            
            <Button variant="outline" size="sm" onClick={handlePrint} data-testid="btn-print">
              <Printer className="h-4 w-4 mr-2" />
              Print
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportExcel} data-testid="btn-export-excel">
              <FileSpreadsheet className="h-4 w-4 mr-2" />
              Export to Excel
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportPDF} data-testid="btn-export-pdf">
              <FileText className="h-4 w-4 mr-2" />
              Export to PDF
            </Button>
            
            <div className="h-6 w-px bg-border mx-2" />
            
            <Button variant="destructive" size="sm" onClick={handleExit} data-testid="btn-exit">
              <X className="h-4 w-4 mr-2" />
              Exit
            </Button>
          </div>

          {/* Date Filters */}
          <GroupBox label="Select Match / Session">
            <div className="flex items-center gap-4 mb-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Label className="text-xs whitespace-nowrap">From:</Label>
                <Input 
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="h-8 w-36"
                  data-testid="filter-date-from"
                />
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs whitespace-nowrap">To:</Label>
                <Input 
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="h-8 w-36"
                  data-testid="filter-date-to"
                />
              </div>
              <Button variant="outline" size="sm" onClick={() => {}} data-testid="btn-filter">
                <Filter className="h-4 w-4" />
              </Button>
              
              <div className="flex items-center gap-2 ml-auto">
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-2 top-2 text-muted-foreground" />
                  <Input 
                    placeholder="Find..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="h-8 w-40 pl-8"
                    data-testid="search-input"
                  />
                </div>
                <Button variant="outline" size="sm" onClick={clearFilters} data-testid="btn-clear">
                  <Trash2 className="h-4 w-4 mr-1" />
                  Clear
                </Button>
              </div>
            </div>

            {/* Main Grid Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Grid 1: Match/Session Selection */}
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-muted px-3 py-2 border-b flex items-center justify-between">
                  <span className="text-sm font-medium">Match / Session</span>
                  <Checkbox 
                    checked={selectedItems.length === filteredDeclaredItems.length && filteredDeclaredItems.length > 0}
                    onCheckedChange={selectAllItems}
                    data-testid="select-all-checkbox"
                  />
                </div>
                <div className="max-h-80 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/50 sticky top-0">
                      <tr>
                        <th className="px-2 py-1 text-left w-8"></th>
                        <th className="px-2 py-1 text-left">Date</th>
                        <th className="px-2 py-1 text-left">Name</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDeclaredItems.map((item, idx) => (
                        <tr 
                          key={item.id}
                          className={`border-b hover:bg-muted/50 cursor-pointer ${selectedItems.includes(item.id) ? 'bg-amber-500/20' : ''}`}
                          onClick={() => toggleItemSelection(item.id)}
                          data-testid={`item-row-${idx}`}
                        >
                          <td className="px-2 py-1">
                            <Checkbox 
                              checked={selectedItems.includes(item.id)}
                              onCheckedChange={() => toggleItemSelection(item.id)}
                            />
                          </td>
                          <td className="px-2 py-1">
                            <div className="text-xs">{item.displayDate}</div>
                            <div className="text-[10px] text-muted-foreground">{item.displayTime}</div>
                          </td>
                          <td className="px-2 py-1">
                            <div className="font-medium">{item.name}</div>
                            <div className="text-[10px] text-muted-foreground">{item.type}</div>
                          </td>
                        </tr>
                      ))}
                      {filteredDeclaredItems.length === 0 && (
                        <tr>
                          <td colSpan={3} className="px-2 py-4 text-center text-muted-foreground">
                            No declared matches/sessions found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="bg-muted px-3 py-2 border-t text-xs">
                  Selected: {selectedItems.length} / {filteredDeclaredItems.length}
                  <Button 
                    size="sm" 
                    className="ml-4 h-6 text-xs"
                    onClick={calculateTrialBalance}
                    disabled={selectedItems.length === 0 || loading}
                    data-testid="btn-calculate"
                  >
                    {loading ? <RefreshCw className="h-3 w-3 animate-spin mr-1" /> : null}
                    Calculate
                  </Button>
                </div>
              </div>

              {/* Grid 2: To Receive */}
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-emerald-600 text-white px-3 py-2 border-b">
                  <span className="text-sm font-medium">To Receive</span>
                </div>
                <div className="max-h-80 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-emerald-50 dark:bg-emerald-950/30 sticky top-0">
                      <tr>
                        <th className="px-2 py-1 text-left">Name</th>
                        <th className="px-2 py-1 text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trialBalanceData.receive.map((item, idx) => (
                        <tr key={item.client_id || idx} className="border-b hover:bg-emerald-50/50 dark:hover:bg-emerald-950/20">
                          <td className="px-2 py-1">
                            <button 
                              className="text-left font-medium text-emerald-700 dark:text-emerald-300 hover:underline cursor-pointer"
                              onClick={() => handleClientClick(item.client_id, item.name)}
                            >
                              {item.name}
                              {item.is_patti && <span className="ml-1 text-[10px] px-1 py-0.5 rounded bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400">P</span>}
                              {item.is_reference_entry && <span className="ml-1 text-[10px] px-1 py-0.5 rounded bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400">R</span>}
                            </button>
                          </td>
                          <td className="px-2 py-1 text-right">
                            <CommissionBreakdownTooltip item={item} type="receive" />
                          </td>
                        </tr>
                      ))}
                      {trialBalanceData.receive.length === 0 && (
                        <tr>
                          <td colSpan={2} className="px-2 py-4 text-center text-muted-foreground">
                            No data
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="bg-emerald-100 dark:bg-emerald-950/50 px-3 py-2 border-t flex justify-between">
                  <span className="text-sm font-medium">Total:</span>
                  <span className="font-mono font-bold text-emerald-700 dark:text-emerald-400">
                    {totalReceive.toFixed(2)}
                  </span>
                </div>
              </div>

              {/* Grid 3: To Pay */}
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-red-600 text-white px-3 py-2 border-b">
                  <span className="text-sm font-medium">To Pay</span>
                </div>
                <div className="max-h-80 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-red-50 dark:bg-red-950/30 sticky top-0">
                      <tr>
                        <th className="px-2 py-1 text-left">Name</th>
                        <th className="px-2 py-1 text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trialBalanceData.pay.map((item, idx) => (
                        <tr key={item.client_id || idx} className="border-b hover:bg-red-50/50 dark:hover:bg-red-950/20">
                          <td className="px-2 py-1">
                            <button 
                              className="text-left font-medium text-red-700 dark:text-red-300 hover:underline cursor-pointer"
                              onClick={() => handleClientClick(item.client_id, item.name)}
                            >
                              {item.name}
                              {item.is_patti && <span className="ml-1 text-[10px] px-1 py-0.5 rounded bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400">P</span>}
                              {item.is_reference_entry && <span className="ml-1 text-[10px] px-1 py-0.5 rounded bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400">R</span>}
                            </button>
                          </td>
                          <td className="px-2 py-1 text-right">
                            <CommissionBreakdownTooltip item={item} type="pay" />
                          </td>
                        </tr>
                      ))}
                      {trialBalanceData.pay.length === 0 && (
                        <tr>
                          <td colSpan={2} className="px-2 py-4 text-center text-muted-foreground">
                            No data
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="bg-red-100 dark:bg-red-950/50 px-3 py-2 border-t flex justify-between">
                  <span className="text-sm font-medium">Total:</span>
                  <span className="font-mono font-bold text-red-700 dark:text-red-400">
                    -{totalPay.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            {/* Final Balance */}
            <div className="mt-4 p-4 bg-card border rounded-lg flex items-center justify-between">
              <span className="text-lg font-semibold">Final Balance:</span>
              <span className={`text-3xl font-bold font-mono ${finalBalance >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                {finalBalance >= 0 ? '' : '-'}{Math.abs(finalBalance).toFixed(2)}
              </span>
            </div>
          </GroupBox>
      </div>

      {/* Transaction Detail Dialog */}
      <TrialBalanceDetailDialog
        open={clientTxDialog.open}
        onClose={() => setClientTxDialog({ 
          open: false, 
          clientId: null, 
          clientName: null, 
          matchIds: [], 
          sessionIds: [], 
          trialBalanceItem: null 
        })}
        clientId={clientTxDialog.clientId}
        clientName={clientTxDialog.clientName}
        matchIds={clientTxDialog.matchIds || []}
        sessionIds={clientTxDialog.sessionIds || []}
        trialBalanceItem={clientTxDialog.trialBalanceItem}
      />
    </div>
  );
}
